// The shell's catch-all route (registered as `'/*'` — see pages/index.js +
// context.js). Replaces canvas's own `'/*': main` catch-all (dropped from
// the merged route map in context.js).
//
// History: 2026-07-28, per Nika, project CANVAS routes were served IN-SHELL.
// 2026-10-09 (CANVAS P0) reversed that and handed project paths to a separate
// canvas origin, because the editor's board frames were same-origin and
// unsandboxed. CANVAS H1 (62729615a and before) closed that reach at its root:
// every canvas user-code mount now runs in an opaque, sandboxed frame that
// boots its own runtime (shared functions/iframeApp/userCodeFrame.js, the
// runtime from this shell's `userCodeRuntimeText`), so no project code runs
// with the shell's origin (canvas/__tests__/boardFrameIsUserCodeOrigin.test.js).
// 2026-10-21, per Nika ("remove independent canvas deployments ... purely rely
// on workspace"): the shell is again the ONLY host of the editor. A project-
// shaped path (`/<owner>/<key>`, `/w/<org>/p/<key>`, classified by
// isCanvasProjectPath) mounts the full canvas editor here; the old canvas
// hosts answer a 308 to this same path on the shell (canvas/redirectWorker.js).
//
// The #15 bundle carve stays: the editor graph (canvas context barrels) stays
// OUT of the eager shell chunk and loads as a dynamic-import chunk only when a
// project path is visited. Mechanism: the same add-missing fold as
// lazySharedLibPage (pages/lazyPage.js — components, functions, seed state)
// PLUS the editor's element methods and globalScope — the editor's methods
// (activateSelected, getCanvasScope, …) live in canvas/methods and the shell's
// eager slice carries only a sliver.
//
// Anything NOT project-shaped keeps the plain not-found, still gated by the
// `_appRoutesSettled` latch so a hard load on a not-yet-injected app route
// (`/w/<org>/crm`) never flashes a false 404 (workspace 45ad2f40).

import { applyMetadata } from '@symbo.ls/helmet'
import { isCanvasProjectPath } from '../functions/canvasProjectRedirect.js'

const _shellPath = () => (typeof window !== 'undefined' ? window.location.pathname : '')

const _foldMissing = (target, src) => {
  if (!target || !src) return
  for (const k in src) if (!(k in target)) target[k] = src[k]
}

// Mount the canvas editor page IN the shell. The imports are async, so the
// router can navigate away while they are in flight: the late-resolve guard of
// lazySharedLibPage applies (disposed -> never; not attached yet -> when it is).
const _mountCanvasEditor = async (el) => {
  const _place = (def) => {
    if (!el.__ref) return
    if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
    else el.set(def)
  }
  try {
    const [ctxMod, pageMod, smblsLib] = await Promise.all([
      import('@symbo-ls/workspace-canvas/context.js'),
      import('@symbo-ls/workspace-canvas/pages/main.js'),
      // DYNAMIC on purpose: a static smbls import would re-add smbls to the
      // EAGER shell chunk (the #15 carve). An smbls build without
      // refreshContextProto just skips the proto refresh below.
      import('smbls').catch(() => null)
    ])
    const lib = (ctxMod && ctxMod.default) || ctxMod
    if (!lib || (!lib.components && !lib.functions)) {
      throw new Error('canvas context resolved empty — stale/mismatched bundle (rebuild needed)')
    }
    if (!pageMod || !pageMod.main) throw new Error('canvas editor page resolved empty')
    const root = el.getRoot?.() || el
    const ctx = root.context
    // Add-missing: the eager slice / an earlier fold always wins.
    _foldMissing(ctx?.components, lib.components)
    _foldMissing(ctx?.functions, lib.functions)
    _foldMissing(ctx?.methods, lib.methods)
    // The per-context element prototype was CACHED at boot (smbls
    // getContextProto), so folded canvas methods (`canvas<Name>`, which
    // shared's same-named methods hand off to) reach `el.call('name')` (live
    // lookup) but not DIRECT `el.method()` calls until refreshContextProto
    // re-syncs the cached proto with the registry.
    if (typeof smblsLib?.refreshContextProto === 'function') smblsLib.refreshContextProto(ctx)
    if (lib.globalScope && ctx) {
      if (!ctx.globalScope) ctx.globalScope = {}
      _foldMissing(ctx.globalScope, lib.globalScope)
    }
    if (lib.state) {
      const seed = {}
      for (const k in lib.state) if (root[k] === undefined) seed[k] = lib.state[k]
      if (Object.keys(seed).length) root.update?.(seed)
    }
    _place({ ...pageMod.main, height: '100%', minHeight: '100%' })
  } catch (err) {
    console.error('[projectRedirect] canvas editor load failed:', err?.message || err)
    _place({
      tag: 'div',
      padding: 'B',
      color: 'caption',
      text: '{{ shell.errorState.lazyPage | polyglot }}'
    })
  }
}

export const projectRedirect = {
  // Placeholder title until the editor page applies its own project title
  // (canvas/pages/main.js metadata).
  //
  // The "Not found" title follows the SAME condition as NotFoundBody's copy
  // (`scope.notFoundShown`). It used to be claimed at mount, inside the very
  // window the copy stays hidden in, so a route that resolved a moment later
  // left the tab on "Not found" whenever its page had no title of its own
  // (vault.pr /vault-intel, PM close-audit of
  // WS-PREBOOT-NOT-FOUND-FLASH-ON-MODULE-ROUTE-1, 2026-09-25). An empty
  // claim leaves the tab as it is. Helmet re-runs this factory when a latch
  // it reads flips, so the tab turns "Not found" at the moment the copy does.
  metadata: (el, s) => {
    if (isCanvasProjectPath(_shellPath()))
      return { title: el.call('polyglot', 'workspace.page.canvas') }
    if (!el.scope?.notFoundShown?.(s?.root || s, el)) return {}
    return { title: el.call('polyglot', 'workspace.page.notFound') }
  },
  tag: 'main',
  flow: 'y',
  align: 'center center',
  minHeight: '100vh',
  gap: 'Z1',
  background: 'canvas',
  color: 'title',
  textAlign: 'center',

  onCreate: (el) => {
    if (el.__canvasEditorDispatched) return
    if (!isCanvasProjectPath(_shellPath())) return
    el.__canvasEditorDispatched = true
    _mountCanvasEditor(el)
  },

  // WS-PREBOOT-NOT-FOUND-FLASH-ON-MODULE-ROUTE-1: how long NotFoundBody stays
  // hidden for a route that has not settled yet, before it gives up and shows
  // a genuine 404 anyway. Bounds the wait for a visit with no session token
  // (the loaders that latch `_appRoutesSettled` never run, so that latch alone
  // would hide a truly dead link forever) — long enough to cover the measured
  // module-route injection race (2-3s live on vault.pr.symbols.app), short
  // enough that a dead link still 404s promptly.
  scope: {
    NOT_FOUND_GRACE_MS: 3000,
    // HALF ONE — the route table is FINAL: nothing still loading can add the
    // path. A signed-in member's app registry has a real "done" signal, the
    // `_appRoutesSettled` latch (the module/apps loaders set it on success, on
    // a definitive failure and on the converge give-up), so a signed-in visit
    // waits for THAT and never for the timer. The grace window bounds only a
    // visit with NO session, where those loaders never run. Measured
    // 2026-09-30 on dev c9f65a38d: a cold load of /w/unreal/default/crm
    // mounted the shell at 7.8 s and injected /crm at 26.8 s, and the 3 s
    // timer had put "Not found" in the tab (and, once the loader lifted, on
    // screen) at ~11 s, for a route the loader re-dispatched 16 s later.
    // A session that lands after the window turns the wait back on.
    routeTableFinal: (root) =>
      !!(root && (root._appRoutesSettled || (!root.signedIn && root._notFoundGraceElapsed))),
    // HALF TWO — a route in the LIVE table claims this URL, so this page is
    // only stranded: the loader injected the route after the router had
    // resolved, and its re-dispatch (functions/workspaceProjects.js
    // `_rerouteIfStranded`, the same own-key test) is on the way. The latch
    // is published one beat BEFORE that re-dispatch, and in a visible tab the
    // router fades this page out before it mounts the next (app.js
    // `onRouteExit`), so without this half "Not found" painted over a route
    // that had just resolved. The URL is read prefix-stripped, like the
    // router matches it (`/w/<org>/<ws>/crm` → `/crm`).
    routeClaimsUrl: (el) => {
      const raw = _shellPath()
      const stripped =
        (el && typeof el.call === 'function' && el.call('stripWorkspacePrefix', raw)) || raw
      const path = String(stripped).split(/[?#]/)[0] || '/'
      if (path === '/*') return false
      const own = (bag) =>
        !!bag && typeof bag === 'object' && Object.prototype.hasOwnProperty.call(bag, path)
      const root = el && typeof el.getRoot === 'function' ? el.getRoot() : null
      return own(el && el.context && el.context.pages) || own(root && root.routes)
    },
    // THE ONE CONDITION for "Not found is on screen": the table is final AND
    // no route in it claims this URL. NotFoundBody's `hide` and the tab title
    // (`metadata`) both read it, so the copy and the tab cannot disagree.
    notFoundShown: (root, el) =>
      !!(el && el.scope && el.scope.routeTableFinal(root) && !el.scope.routeClaimsUrl(el))
  },

  onInit: (el) => {
    el.scope.notFoundGraceTimer = null
    // A project path hosts the canvas editor (onCreate), never a 404.
    el.scope.hostsNotFound = !isCanvasProjectPath(_shellPath())
    if (!el.scope.hostsNotFound) return
    el.scope.notFoundGraceTimer = setTimeout(() => {
      // The ROOT STATE — the store NotFoundBody's `hide` reads as `s.root`.
      // Never `el.getRoot()` alone: that is the root ELEMENT, and an element
      // update() keeps the key as an element prop that no `hide` reads. A
      // smbls mount measured exactly that: the flag landed on the element
      // and the body stayed hidden for good.
      const root = el.state?.root || el.state || el.getRoot?.()?.state
      root?.update?.({ _notFoundGraceElapsed: true }, { preventFetch: true })
    }, el.scope.NOT_FOUND_GRACE_MS)
  },

  onRemove: (el) => {
    clearTimeout(el.scope.notFoundGraceTimer)
    // Hand the tab back. The router removes this page BEFORE it mounts the
    // next one, and a page with no title of its own keeps whatever the tab
    // shows — so a "Not found" claimed here outlived the catch-all onto the
    // page that resolved (a module route injected after the grace window).
    // The neutral brand title goes in first; the next page's own title, when
    // it has one, replaces it as that page mounts. Half one only: the route
    // replacing this page claims the URL by now, so the full condition reads
    // false exactly when a claimed tab is owed back.
    if (!el.scope.hostsNotFound || !el.scope.routeTableFinal(el.state?.root || el.state)) return
    const doc = el.context?.document || (typeof document !== 'undefined' ? document : null)
    if (doc) applyMetadata({ title: el.call('brandName') }, doc)
  },

  NotFoundBody: {
    flow: 'y',
    align: 'center center',
    gap: 'Z1',
    // Never show not-found CONTENT for a path that might still resolve:
    //  - project-shaped paths are the editor dispatch above (never a 404);
    //  - injected app/module routes don't exist at router boot, so a hard
    //    load lands here before `_ensureWorkspaceModule` has injected them —
    //    `_appRoutesSettled` latches once the loaders finish (success OR
    //    failure), so a genuinely nonexistent path still 404s after settle.
    //    A "pending" flag was tried first and RACED `_authReady` — the latch
    //    is absent-until-done, so it has no gap (workspace 45ad2f40).
    //  - a signed-out visit never runs those loaders, so `_appRoutesSettled`
    //    alone would hide this forever for it — `onInit` above ALSO releases
    //    it once `_notFoundGraceElapsed` fires, bounding the wait instead of
    //    reading a same-instant session-token snapshot (which reads exactly
    //    as "signed out" during the very race this fix targets, live on a
    //    custom domain host — WS-PREBOOT-NOT-FOUND-FLASH-ON-MODULE-ROUTE-1).
    subscribeTo: ['_appRoutesSettled', '_notFoundGraceElapsed', 'signedIn'],
    // The page's `scope` (borrowed here) holds the shared predicate.
    hide: (el, s) => isCanvasProjectPath(_shellPath()) || !el.scope.notFoundShown(s.root || s, el),

    Heading: {
      tag: 'h1',
      margin: '0',
      fontSize: 'B1',
      fontWeight: '700',
      color: 'title',
      text: '{{ workspace.notFound.title | polyglot }}'
    },
    Caption: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'caption',
      text: '{{ workspace.notFound.caption | polyglot }}'
    },
    HomeLink: {
      extends: 'Link',
      href: '/',
      // `blue` is a raw HUE token — one fixed value, no light/dark pair — so it
      // measured 3.62:1 on the light shell (brand #0085FF on white), under the
      // 4.5 floor for this 700-weight Z2 text. `accentInk` is the same hue
      // re-toned per theme (symbols-mcp Rule 69). Found by the aa-contrast
      // sweep on /main and /system, 2026-08-09.
      color: 'accentInk',
      fontSize: 'Z2',
      fontWeight: '700',
      text: '{{ workspace.notFound.goHome | polyglot }}'
    }
  }
}
